import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  alpha,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { contactPath, crmReturnState } from '../crmPaths';
import {
  columnById,
  ContactListSort,
  describeSupportLevel,
  formatDate,
  formatRate,
  matchHints,
} from './contactList';
import { SOURCE_LABELS } from './filters/filterModel';
import { ContactRow } from './useContactPage';

/** Tags shown in a cell before "+N". */
const TAGS_SHOWN = 3;

/** The class of the name column's cells, which stay put when the table scrolls sideways. */
const STICKY = 'crm-contacts-sticky';

/** An empty cell: a faint dash, so the eye can tell "none" from "not loaded". */
const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

const orNone = (value: React.ReactNode) => (value === null || value === undefined || value === '' ? NONE : value);

const GATES = [
  { key: 'canEmail', label: 'email', Icon: MailOutlineIcon },
  { key: 'canCall', label: 'call', Icon: PhoneOutlinedIcon },
  { key: 'canText', label: 'text', Icon: SmsOutlinedIcon },
] as const;

/** One gate: green when the server allows it, struck through when not. */
const Gate = ({ allowed, label, Icon }: { allowed: boolean; label: string; Icon: typeof MailOutlineIcon }) => {
  const words = `${allowed ? 'Can' : 'Cannot'} ${label}`;
  return (
    <Tooltip title={words}>
      <Box
        component="span"
        role="img"
        aria-label={words}
        sx={{
          position: 'relative',
          width: 26,
          height: 26,
          borderRadius: 1.5,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: allowed ? 'rgba(22, 163, 74, 0.12)' : '#f1f5f9',
          color: allowed ? '#15803d' : '#94a3b8',
          '& svg': { fontSize: 16 },
        }}
      >
        <Icon />
        {!allowed && (
          <Box
            component="span"
            aria-hidden
            sx={{ position: 'absolute', width: 20, height: '1.5px', bgcolor: '#94a3b8', transform: 'rotate(-45deg)' }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

/** The three gates the server worked out (§1), and the two hard flags behind them. */
const ConsentCell = ({ row }: { row: ContactRow }) => (
  <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 0.5 }}>
    {GATES.map(({ key, label, Icon }) => (
      <Gate key={key} allowed={row[key]} label={label} Icon={Icon} />
    ))}
    {row.doNotContact && (
      <Chip size="small" color="error" label="Do not contact" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }} />
    )}
    {row.doNotCall && !row.doNotContact && (
      <Chip size="small" color="error" variant="outlined" label="Do not call" sx={{ height: 22, fontSize: '0.75rem' }} />
    )}
  </Box>
);

const TagsCell = ({ tags }: { tags: ContactRow['tags'] }) => {
  if (!tags?.length) return NONE;

  const rest = tags.slice(TAGS_SHOWN);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
      {tags.slice(0, TAGS_SHOWN).map((tag) => (
        <Chip
          key={tag.tagId}
          size="small"
          label={tag.name}
          title={tag.name}
          sx={{
            height: 22,
            maxWidth: 160,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: tag.color ?? 'text.primary',
            bgcolor: tag.color ? alpha(tag.color, 0.1) : '#f1f5f9',
            border: '1px solid',
            borderColor: tag.color ? alpha(tag.color, 0.35) : 'divider',
          }}
        />
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.map((tag) => tag.name).join(', ')}>
          <Chip size="small" label={`+${rest.length}`} sx={{ height: 22, fontSize: '0.75rem' }} />
        </Tooltip>
      )}
    </Box>
  );
};

const nameOf = (row: ContactRow) => row.fullName ?? row.primaryEmail ?? `Contact #${row.id}`;

const placeOf = (row: ContactRow) => [row.city, row.region, row.countryCode].filter(Boolean).join(', ');

/** Soft background and text pairs; a contact keeps its colour by id. */
const AVATAR_COLOURS: Array<[string, string]> = [
  ['#e0e7ff', '#3730a3'],
  ['#dcfce7', '#166534'],
  ['#fef3c7', '#92400e'],
  ['#fce7f3', '#9d174d'],
  ['#e0f2fe', '#075985'],
  ['#ede9fe', '#5b21b6'],
  ['#ffedd5', '#9a3412'],
  ['#ccfbf1', '#115e59'],
];

const initialsOf = (row: ContactRow) => {
  const words = (row.fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length) return `${words[0][0]}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  return (row.primaryEmail?.[0] ?? '#').toUpperCase();
};

/** Tick boxes for bulk changes (card 19), in the name column so they stay in view. */
export interface TableSelection {
  isSelected: (id: number) => boolean;
  onToggle: (id: number) => void;
  /** The header box: none, some or all of this page ticked. */
  page: 'none' | 'some' | 'all';
  onTogglePage: () => void;
}

interface ContactTableProps {
  rows: Array<ContactRow>;
  columns: ReadonlyArray<string>;
  sort: ContactListSort | null;
  onSort: (columnId: string) => void;
  /** Show why each row matched: only while the list is searching (§4). */
  searching: boolean;
  /** Dims the rows while the next page is on its way. */
  stale: boolean;
  selection?: TableSelection;
}

const BOX_SX = { p: 0.5, ml: -0.75, '& svg': { fontSize: 20 } } as const;

export const ContactTable = ({ rows, columns, sort, onSort, searching, stale, selection }: ContactTableProps) => {
  const here = useLocation();

  const cell = (row: ContactRow, columnId: string): React.ReactNode => {
    switch (columnId) {
      case 'name': {
        const [background, colour] = AVATAR_COLOURS[row.id % AVATAR_COLOURS.length];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {selection && (
              <Checkbox
                size="small"
                checked={selection.isSelected(row.id)}
                onChange={() => selection.onToggle(row.id)}
                inputProps={{ 'aria-label': `Select ${nameOf(row)}` }}
                sx={BOX_SX}
              />
            )}
            <Avatar aria-hidden sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 600, bgcolor: background, color: colour }}>
              {initialsOf(row)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Link
                component={RouterLink}
                to={contactPath(row.id)}
                state={crmReturnState(here)}
                underline="hover"
                sx={{ fontWeight: 600, color: 'text.primary', '&:hover': { color: 'primary.main' } }}
              >
                {nameOf(row)}
              </Link>
              {searching &&
                matchHints(row.matchedOn).map((hint) => (
                  <Typography key={hint} variant="caption" color="warning.dark" component="div">
                    {hint}
                  </Typography>
                ))}
            </Box>
          </Box>
        );
      }
      case 'email':
        return orNone(row.primaryEmail);
      case 'phone':
        return orNone(row.primaryPhone);
      case 'location':
        return orNone(placeOf(row));
      case 'consent':
        return <ConsentCell row={row} />;
      case 'tags':
        return <TagsCell tags={row.tags} />;
      case 'calls':
        return row.callCount.toLocaleString('en-US');
      case 'lastCalled':
        return orNone(formatDate(row.lastCalledAt));
      case 'owner':
        return orNone(row.owner ? `${row.owner.firstName} ${row.owner.lastName}`.trim() : '');
      case 'support':
        return orNone(describeSupportLevel(row.supportLevel));
      case 'stage':
        return orNone(row.lifecycleStage);
      case 'source':
        return orNone(row.source ? SOURCE_LABELS[row.source] ?? row.source : '');
      case 'lastContacted':
        return orNone(formatDate(row.lastContactedAt));
      case 'engagement':
        return orNone(row.engagement ? formatRate(row.engagement.engagementRate) : '');
      case 'memberSince':
        return orNone(formatDate(row.nbCreatedAt));
      case 'added':
        return orNone(formatDate(row.createdAt));
      case 'joined':
        return orNone(formatDate(row.joinedAt));
      default:
        return null;
    }
  };

  return (
    <TableContainer
      sx={{
        maxHeight: '70vh',
        opacity: stale ? 0.55 : 1,
        transition: 'opacity 120ms',
        '& .MuiTableCell-root': { borderColor: '#eef1f5' },
        '& .MuiTableCell-head': { color: 'text.secondary', fontSize: '0.75rem', letterSpacing: '0.02em', py: 1.25 },
        // One line a row: a wide table scrolls sideways rather than growing tall.
        '& .MuiTableCell-body': { py: 1, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
        '& tbody tr:last-of-type td': { borderBottom: 0 },
        '& .MuiTableRow-hover:hover': { backgroundColor: '#f8fafc' },
        // The name stays in view while the other columns scroll under it.
        [`& .${STICKY}`]: {
          position: 'sticky',
          left: 0,
          zIndex: 1,
          backgroundColor: '#ffffff',
          boxShadow: 'inset -1px 0 0 #eef1f5',
        },
        [`& .MuiTableRow-hover:hover .${STICKY}`]: { backgroundColor: '#f8fafc' },
        // A ticked row is tinted, its name cell too (the sticky cell has its own background).
        '& .MuiTableRow-root.Mui-selected, & .MuiTableRow-root.Mui-selected:hover': { backgroundColor: '#eef4ff' },
        [`& .MuiTableRow-root.Mui-selected .${STICKY}`]: { backgroundColor: '#eef4ff' },
        [`& .MuiTableCell-head.${STICKY}`]: { zIndex: 3, backgroundColor: '#f8fafc' },
      }}
    >
      <Table stickyHeader size="small" aria-label="Contacts" aria-busy={stale}>
        <TableHead>
          <TableRow>
            {columns.map((id) => {
              const column = columnById(id);
              if (!column) return null;

              const active = sort?.column === id;

              return (
                <TableCell
                  key={id}
                  sortDirection={active ? sort?.direction : false}
                  className={id === 'name' ? STICKY : undefined}
                  sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                >
                  {id === 'name' && selection && (
                    <Tooltip title={selection.page === 'all' ? 'Untick this page' : 'Tick every contact on this page'}>
                      <Checkbox
                        size="small"
                        checked={selection.page === 'all'}
                        indeterminate={selection.page === 'some'}
                        onChange={selection.onTogglePage}
                        inputProps={{ 'aria-label': 'Select every contact on this page' }}
                        sx={{ ...BOX_SX, mr: 0.75 }}
                      />
                    </Tooltip>
                  )}
                  {column.sortFields ? (
                    <TableSortLabel
                      active={active}
                      direction={active ? sort?.direction : 'asc'}
                      onClick={() => onSort(id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover selected={selection?.isSelected(row.id) ?? false}>
              {columns.map((id) => (
                <TableCell key={id} className={id === 'name' ? STICKY : undefined}>
                  {cell(row, id)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
